import type { EditionReadModel } from "@apf/site-publication";
import { ProofBadge } from "../components/Shell.tsx";

export function AccentureLanding({ model }: { model: EditionReadModel }) {
  const { products, expression, edition_ } = model as typeof model & { edition_: NonNullable<typeof model["edition_"]> };

  if (!edition_) {
    return (
      <div className="wrap" style={{ paddingTop: 96 }}>
        <p>Accenture Edition content is pending admission.</p>
      </div>
    );
  }

  return (
    <>
      {/* Hero — on --hero #2D004A */}
      <section
        className="site-hero"
        aria-label="WORKABLE, Accenture Edition introduction"
        data-testid="accenture-hero"
      >
        <div className="wrap" style={{ paddingTop: 72, paddingBottom: 72 }}>
          {/* Accenture logo on white plate */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "white",
              padding: "16px 22px",
              marginBottom: 32,
              width: 180,
              height: 72,
            }}
          >
            <img src="/brand/accenture-logo.svg" alt="Accenture" style={{ width: "100%", maxHeight: 44 }} />
          </div>
          <span className="eyebrow">{edition_.hero.eyebrow}</span>
          <h1 style={{ maxWidth: 880 }}>{edition_.hero.headline}</h1>
          <p style={{ maxWidth: 640, marginBottom: 32 }}>{edition_.hero.lede}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href={edition_.hero.primaryCta.href} className="btn primary">{edition_.hero.primaryCta.label}</a>
            {edition_.hero.secondaryCta && (
              <a href={edition_.hero.secondaryCta.href} className="btn ghost">{edition_.hero.secondaryCta.label}</a>
            )}
          </div>
        </div>
      </section>

      {/* Method */}
      <section className="section" aria-labelledby="method-heading" data-testid="method-section">
        <div className="wrap">
          <h2 id="method-heading">Accenture's method over Core.</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 28, marginTop: 32 }}>
            {edition_.method.map((m, i) => (
              <div key={m.id} style={{ borderTop: "2px solid var(--brand)", paddingTop: 20 }}>
                <span className="small" style={{ display: "block", marginBottom: 12 }}>0{i + 1}</span>
                <h3 style={{ fontSize: 18, marginBottom: 10 }}>{m.title}</h3>
                <p style={{ fontSize: 14, color: "var(--muted)" }}>{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client decision boundary */}
      <section className="section" aria-labelledby="boundary-heading" data-testid="boundary-section">
        <div className="wrap">
          <h2 id="boundary-heading">Who decides what.</h2>
          <div className="grid-3 boundary">
            {[
              { label: "Client owns", items: edition_.clientDecisionBoundary.clientOwns },
              { label: "Accenture owns", items: edition_.clientDecisionBoundary.accentureOwns },
              { label: "Core owns", items: edition_.clientDecisionBoundary.coreOwns },
            ].map(({ label, items }, i) => (
              <div
                key={label}
                style={{
                  padding: 28,
                  background: i === 1 ? "var(--brandwash)" : "var(--surface)",
                }}
              >
                <p className="eyebrow">{label}</p>
                <ul style={{ paddingLeft: 18, margin: 0, fontSize: 14, color: "var(--muted)" }}>
                  {items.map((item) => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement outputs */}
      <section className="section" aria-labelledby="outputs-heading" data-testid="outputs-section">
        <div className="wrap">
          <h2 id="outputs-heading">What the team takes forward.</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24, marginTop: 32 }}>
            {edition_.engagementOutputs.map((out) => (
              <div key={out.id} style={{ padding: 24, border: "1px solid var(--line)", background: "var(--surface)" }}>
                <h3 style={{ fontSize: 18, marginBottom: 10 }}>{out.title}</h3>
                <p style={{ fontSize: 14, color: "var(--muted)" }}>{out.body}</p>
                {out.coreRefs.length > 0 && (
                  <p className="small" style={{ marginTop: 16 }}>
                    Built on {out.coreRefs.map((id) => products.constructs.find((c) => c.id === id)?.name ?? id).join(" · ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      {edition_.proof.length > 0 && (
        <section className="section" aria-labelledby="proof-heading">
          <div className="wrap">
            <h2 id="proof-heading">Standing.</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24, marginTop: 32 }}>
              {edition_.proof.map((p) => (
                <div key={p.id} style={{ borderTop: "2px solid var(--ink)", paddingTop: 20 }}>
                  <ProofBadge proofClass={p.proofClass} />
                  <p style={{ marginTop: 12, fontSize: 14 }}>{p.claim}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Built on WORKABLE Core — same nine Construct cards, with Accenture adaptation labels */}
      <section className="section" aria-labelledby="core-heading" style={{ borderBottom: "none" }}>
        <div className="wrap">
          <h2 id="core-heading">Built on WORKABLE Core.</h2>
          <p style={{ color: "var(--muted)", maxWidth: 520, marginBottom: 32 }}>
            The same nine products. Accenture method applied where it changes the outcome.
          </p>
          <div className="product-grid" data-testid="accenture-construct-grid">
            {products.constructs.map((c, i) => {
              const adaptation = edition_.constructAdaptations.find((a) => a.coreRef === c.id);
              return (
                <a key={c.id} href={`/accenture-edition/constructs/${c.slug}`} className="product-card">
                  <span className="ordinal">0{i + 1}</span>
                  <span className="sig">{adaptation?.accentureLabel ?? c.signature}</span>
                  <h3>{c.functionLabel}</h3>
                  <p style={{ fontSize: 14, color: "var(--muted)" }}>
                    {adaptation?.methodNote || c.descriptor}
                  </p>
                  <div className="card-bottom">
                    <div className="module-bar">
                      {c.modules.map((m) => (
                        <div key={m.id} className={`module-pip ${m.standing}`} title={m.name} />
                      ))}
                    </div>
                    <ProofBadge proofClass={c.proofClass} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
